import { timingIntent, mergeTimingOwnership } from './orderTiming2'
export const GROUP_REVIEW_HANDOFF_VERSION = 1
export const GROUP_REVIEW_HANDOFF_SIZE_BUDGET = 750000

const CUSTOMER_FIELDS = ['customer_id', 'internal_id', 'customer_number', 'customer_name', 'customer_email', 'pricing_level_code', 'price_level_code', 'sales_rep_id', 'sales_rep_number', 'rep_number', 'address_1', 'address_2', 'city', 'state', 'zip_code', 'country']
const SHIP_FIELDS = ['ship_to_id', 'ship_to_number', 'ship_name', 'contact_name', 'attn', 'address_1', 'address_2', 'city', 'state', 'zip_code', 'country', 'email', 'phone']
const PROGRAM_FIELDS = ['allow_blanks', 'program_id', 'program_name', 'price_level_code', 'is_licensed', 'one_style_per', 'rush_option', 'ribbon_option', 'hangtags_option', 'string_tags_option', 'days_out', 'allow_fastlane']
const STYLE_FIELDS = ['band_option', 'rope_option', 'leather_option', 'style_id', 'style_name', 'style_number', 'style_description', 'product_class', 'sku', 'color_code', 'size_code']
const TECHNIQUE_FIELDS = ['technique_id', 'internal_id', 'technique_name', 'canonical_technique_name', 'semantic_technique_key', 'contributing_technique_ids', 'program_ids', 'program_techniques', 'cost', 'technique_cost', 'distributor_cost', 'min_quantity', 'new_min_quantity', 'under_min_quantity_upcharge', 'free_decorations', 'lead_time', 'split_order_key', 'allow_fastlane']
const LOCATION_FIELDS = ['location_id', 'internal_id', 'location_internal_id', 'location_name', 'location_code', 'contributing_location_ids', 'contributing_technique_ids', 'contributing_program_ids', 'contributing_ptl_tuples', 'ptl_identities', 'product_class', 'cost', 'technique_cost', 'free_decorations', 'location_for_free_decoration_matters', 'ptl_program_id', 'ptl_technique_id', 'ptl_location_id', 'ptl_identity']
const ARTWORK_FIELDS = ['reference_search', 'ref', 'workflow_uploads_id', 'id', 'media_id', 'name', 'file_name', 'filename', 'tape_number', 'description', 'req_type', 'temp_upload', 'temp_upload_id', 'preview_token', 'temp_disk', 'temp_path', 'mime_type', 'type', 'size', 'uploaded_at', 'expires_at']
const COLOR_FIELDS = ['band', 'rope', 'leather', 'minimum_color_id', 'color_id', 'color_name', 'color_code', 'customer_color_name', 'customer_color_code', 'variant_label', 'instructions']
const SIZE_FIELDS = ['available_quantity', 'size_id', 'size_name', 'size_code', 'quantity', 'unit_price', 'cost', 'blank', 'blank_dist', 'price_min_12', 'price_min_24', 'price_min_36', 'price_min_72', 'distr_min_12', 'distr_min_24', 'distr_min_36', 'distr_min_72'].concat(Array.from({ length: 20 }, (_, index) => 'price_' + String(index + 1).padStart(2, '0')))
const CONVERSION_FIELDS = ['contract_version', 'source_type', 'source_saved_order_id', 'source_updated_at', 'converted_at', 'source_fingerprint']
const PREBOOK_FIELDS = ['id', 'prebook_id', 'pre_book_label', 'sandbox_discount_id', 'live_discount_id', 'discount_percent']

function pick (source, fields) {
  if (source === null || source === undefined) return ''
  if (typeof source !== 'object') return source
  return fields.reduce((result, field) => {
    if (source[field] !== undefined && source[field] !== null) result[field] = source[field]
    return result
  }, {})
}

function safeArtworkReference (artwork) {
  const selected = pick(artwork, ARTWORK_FIELDS)
  Object.keys(selected).forEach(key => {
    const value = selected[key]
    if (typeof value === 'string' && (/^data:/i.test(value) || value.length > 4096)) delete selected[key]
  })
  return selected
}

function sizeDto (size) {
  const result = pick(size, SIZE_FIELDS)
  if (size && size.minimum_adjustment) result.minimum_adjustment = pick(size.minimum_adjustment, ['from', 'to'])
  return result
}

function colorDto (color) {
  return Object.assign(pick(color, COLOR_FIELDS), {
    sizes: (color && color.sizes || []).map(sizeDto),
    inner_colors: (color && color.inner_colors || []).map(colorDto)
  })
}

function placementDto (placement) {
  const result = {
    id: placement && (placement.bundle_placement_id || placement.id) || '',
    bundle_placement_id: placement && (placement.bundle_placement_id || placement.id) || '',
    name: placement && placement.name || '',
    placement_label: placement && placement.placement_label || '',
    design_mode: placement && placement.design_mode || '',
    reference_search_recovery: placement && placement.reference_search_recovery || '',
    artwork_usage_mode: placement && placement.artwork_usage_mode || '',
    broader_technique_selection: !!(placement && placement.broader_technique_selection),
    artwork_name: placement && placement.artwork_name || '',
    artwork: safeArtworkReference(placement && placement.artwork),
    color_type: placement && typeof placement.color_type === 'string' ? placement.color_type : '',
    preferred_color_type: placement && typeof placement.preferred_color_type === 'string' ? placement.preferred_color_type : '',
    color_type_origin: placement && placement.color_type_origin || '',
    locked: !!(placement && placement.locked),
    decoration_text: placement && placement.decoration_text || '',
    notes: placement && typeof placement.notes === 'string' ? placement.notes : '',
    required: !!(placement && placement.required),
    requires_new_art: !!(placement && placement.requires_new_art),
    technique: pick(placement && placement.technique, TECHNIQUE_FIELDS),
    technique_intent: placement && placement.technique_intent ? pick(placement.technique_intent, ['internal_id', 'technique_name', 'canonical_technique_name', 'semantic_technique_key']) : null,
    fallback_preferences: (placement && placement.fallback_preferences || []).map(preference => pick(preference, ['section_id', 'original_internal_id', 'original_canonical_technique_name', 'location_internal_id', 'fallback_canonical_technique_name', 'fallback_technique_name'])),
    location: pick(placement && placement.location, LOCATION_FIELDS),
    source_technique_hint: pick(placement && placement.source_technique_hint, TECHNIQUE_FIELDS),
    source_location_hint: pick(placement && placement.source_location_hint, LOCATION_FIELDS),
    resolution_status: placement && placement.resolution_status || '',
    resolution_source: placement && placement.resolution_source || '',
    resolution_type: placement && placement.resolution_type || '',
    selected_product_class: placement && placement.selected_product_class || '',
    configuration_key: placement && placement.configuration_key || '',
    ptl_identity: placement && placement.ptl_identity || '',
    ptl_program_id: placement && placement.ptl_program_id || null,
    ptl_technique_id: placement && placement.ptl_technique_id || null,
    ptl_location_id: placement && placement.ptl_location_id || null,
    canonical_technique_internal_id: placement && placement.canonical_technique_internal_id || null,
    canonical_location_internal_id: placement && placement.canonical_location_internal_id || null,
    artwork_configuration_changed: !!(placement && placement.artwork_configuration_changed),
    artwork_configuration_original: placement && placement.artwork_configuration_original ? {
      media_id: placement.artwork_configuration_original.media_id || null,
      technique: pick(placement.artwork_configuration_original.technique, TECHNIQUE_FIELDS),
      location: pick(placement.artwork_configuration_original.location, LOCATION_FIELDS),
      existing: !!placement.artwork_configuration_original.existing
    } : null,
    configuration_changed_since_save: !!(placement && placement.configuration_changed_since_save)
  }
  return result
}

function legacyLocationDto (placement) {
  return {
    bundle_placement_id: placement && (placement.bundle_placement_id || placement.id) || '',
    location: pick(placement && placement.location, LOCATION_FIELDS),
    technique: pick(placement && placement.technique, TECHNIQUE_FIELDS),
    placement_label: placement && placement.placement_label || '',
    file_name: placement && placement.file_name || '',
    decoration: safeArtworkReference(placement && placement.decoration),
    color_type: placement && typeof placement.color_type === 'string' ? placement.color_type : '',
    decoration_text: placement && placement.decoration_text || '',
    text_decoration: placement && placement.text_decoration || 0,
    required: placement && placement.required || 0
  }
}

function keyedPlacementsDto (groups) {
  return Object.keys(groups || {}).reduce((result, key) => {
    result[key] = (groups[key] || []).map(placementDto)
    return result
  }, {})
}

function styleLineDto (line) {
  return {
    ...(line && line.blank_order !== undefined ? { blank_order: line.blank_order } : {}),
    id: line && line.id || '',
    expanded: line && line.expanded !== undefined ? !!line.expanded : true,
    style_info: pick(line && line.style_info, STYLE_FIELDS),
    units: line && line.units || 0,
    colors: (line && line.colors || []).map(colorDto),
    decoration_set_ids: (line && line.decoration_set_ids || []).slice(),
    notes: line && line.notes || '',
    bundle_resolutions: keyedPlacementsDto(line && line.bundle_resolutions)
  }
}

function designDto (design) {
  return {
    id: design && design.id || '',
    expanded: design && design.expanded !== undefined ? !!design.expanded : true,
    name: design && design.name || '',
    customer_number: design && design.customer_number || '',
    style_workspace_initialized: !!(design && design.style_workspace_initialized),
    placements: (design && design.placements || []).map(placementDto)
  }
}

function sectionDto (section) {
  return {
    section_type: section && section.section_type,
    blank_workspace_id: section && section.blank_workspace_id,
    source_group_id: section && section.source_group_id,
    id: section && section.id || '',
    expanded: section && section.expanded !== undefined ? !!section.expanded : true,
    name: section && section.name || '',
    program_info: pick(section && section.program_info, PROGRAM_FIELDS),
    bundle_id: section && section.bundle_id || '',
    license: pick(section && section.license, ['id', 'license_id', 'name']),
    license_design: pick(section && section.license_design, ['id', 'license_design_id', 'name']),
    notes: section && section.notes || '',
    decoration_sets: (section && section.decoration_sets || []).map(placementDto),
    style_lines: (section && section.style_lines || []).map(styleLineDto)
  }
}

export function buildGroupReviewHandoff2 (builderState, legacyState, createdAt) {
  const builder = builderState || {}
  return {
    handoff_version: GROUP_REVIEW_HANDOFF_VERSION,
    created_at: createdAt || new Date().toISOString(),
    builder: {
      active_section_workspace_id: builder.active_section_workspace_id || '',
      blank_recovery: JSON.parse(JSON.stringify(builder.blank_recovery || [])),
      ...(builder.scheduling_intent ? { scheduling_intent: timingIntent(builder.scheduling_intent), scheduling_intent_updated_at: builder.scheduling_intent_updated_at || '', timing_workspace_id: builder.timing_workspace_id || '', fast_lane_accepted_fingerprint: builder.fast_lane_accepted_fingerprint || '', fast_lane_accepted_event_date: builder.fast_lane_accepted_event_date || '' } : {}),
      ...(builder.active_saved_draft && builder.saved_id && builder.saved_updated_at && String(builder.active_saved_draft.saved_id) === String(builder.saved_id) && String(builder.active_saved_draft.saved_updated_at) === String(builder.saved_updated_at) ? {
        active_saved_draft: pick(builder.active_saved_draft, ['saved_id', 'saved_updated_at', 'builder_identity']),
        saved_id: builder.saved_id,
        saved_updated_at: builder.saved_updated_at
      } : { active_saved_draft: null, saved_id: '', saved_updated_at: '' }),
      prebook: pick(builder.prebook, PREBOOK_FIELDS),
      ...(builder.conversion_provenance ? { conversion_provenance: pick(builder.conversion_provenance, CONVERSION_FIELDS) } : {}),
      ...(builder.imported_review_order ? { imported_review_order: {
        prebook: pick(builder.imported_review_order.prebook, PREBOOK_FIELDS),
        available_emails: Array.isArray(builder.imported_review_order.available_emails) ? builder.imported_review_order.available_emails.map(row => typeof row === 'string' ? row : pick(row, ['email', 'checked', 'ship_to_email'])) : [],
        extra_emails: builder.imported_review_order.extra_emails || '',
        email_text: builder.imported_review_order.email_text || ''
      } } : {}),
      po_number: builder.po_number || '',
      ...(Object.prototype.hasOwnProperty.call(builder, 'wait_on_customer_po') ? { wait_on_customer_po: builder.wait_on_customer_po === true || builder.wait_on_customer_po === 1 || builder.wait_on_customer_po === '1' } : {}),
      customer_info: pick(builder.customer_info, CUSTOMER_FIELDS),
      ship_to_info: pick(builder.ship_to_info, SHIP_FIELDS),
      new_ship_to: !!builder.new_ship_to,
      new_ship_info: pick(builder.new_ship_info, SHIP_FIELDS),
      ship_to_default: !!builder.ship_to_default,
      rep: pick(builder.rep, ['id', 'user_id', 'rep_id', 'rep_number', 'name', 'email']),
      entry_mode: 'design_first',
      bundle_step_complete: true,
      bundles: (builder.bundles || []).map(designDto),
      sections: (builder.sections || []).map(sectionDto)
    },
    legacy: {
      order_items: ((legacyState && legacyState.order_items) || []).map(item => ({
        section_id: item.section_id || '',
        line_id: item.line_id || '',
        temp_section_name: item.temp_section_name || '',
        style_number: item.style_number || 0,
        program_info: pick(item.program_info, PROGRAM_FIELDS),
        license: pick(item.license, ['id', 'license_id', 'name']),
        license_design: pick(item.license_design, ['id', 'license_design_id', 'name']),
        style_info: pick(item.style_info, STYLE_FIELDS),
        units: item.units || 0,
        blank_order: item.blank_order || 0,
        section_type: item.section_type,
        notes: item.notes || '',
        order_item_locations: (item.order_item_locations || []).map(legacyLocationDto)
      }))
    }
  }
}

export function restoreGroupReviewHandoff2 (serializedState) {
  const parsed = typeof serializedState === 'string' ? JSON.parse(serializedState) : serializedState
  if (!parsed || parsed.handoff_version !== GROUP_REVIEW_HANDOFF_VERSION || !parsed.builder) throw new Error('Unsupported or invalid group Review handoff.')
  return parsed
}

function stableArrayIdentity (item) {
  if (!item || typeof item !== 'object') return ''
  return item.id || item.bundle_placement_id || item.style_id || item.minimum_color_id || item.color_id || item.size_id || item.ship_to_id || ''
}

export function mergeBuilderRoundTrip2 (builderDraft, reviewBuilder) {
  const merge = (base, current) => {
    if (current === undefined) return base
    if (Array.isArray(current)) {
      if (!Array.isArray(base)) return current.map(item => merge(undefined, item))
      return current.map((item, index) => {
        const identity = stableArrayIdentity(item)
        const prior = identity
          ? base.find(candidate => String(stableArrayIdentity(candidate)) === String(identity))
          : base[index]
        return merge(prior, item)
      })
    }
    if (!current || typeof current !== 'object') return current
    const result = base && typeof base === 'object' && !Array.isArray(base) ? Object.assign({}, base) : {}
    Object.keys(current).forEach(key => { result[key] = merge(result[key], current[key]) })
    return result
  }
  return mergeTimingOwnership(builderDraft || {}, reviewBuilder || {}, merge(builderDraft || {}, reviewBuilder || {}))
}

export function serializedByteLength2 (value) {
  const serialized = typeof value === 'string' ? value : JSON.stringify(value)
  return unescape(encodeURIComponent(serialized)).length
}

export function measureGroupReviewState2 (value) {
  const rows = []
  function visit (current, path) {
    if (!current || typeof current !== 'object') return
    Object.keys(current).forEach(key => {
      const child = current[key]
      const childPath = path ? path + '.' + key : key
      rows.push({ path: childPath, bytes: serializedByteLength2(child) })
      if (child && typeof child === 'object') visit(child, childPath)
    })
  }
  visit(value, '')
  return {
    total_bytes: serializedByteLength2(value),
    designs: ((value && value.builder && value.builder.bundles) || []).map((design, index) => ({ index, bytes: serializedByteLength2(design) })),
    sections: ((value && value.builder && value.builder.sections) || []).map((section, index) => ({ index, bytes: serializedByteLength2(section) })),
    styles: ((value && value.builder && value.builder.sections) || []).reduce((rows, section, sectionIndex) => rows.concat((section.style_lines || []).map((line, lineIndex) => ({ section_index: sectionIndex, line_index: lineIndex, bytes: serializedByteLength2(line) }))), []),
    placements: rows.filter(row => /placements\.\d+$|bundle_resolutions\.[^.]+\.\d+$/.test(row.path)).sort((a, b) => b.bytes - a.bytes).slice(0, 20),
    largest_property_paths: rows.sort((a, b) => b.bytes - a.bytes).slice(0, 20)
  }
}
